import { memo } from "react";
import { useAppSelector } from "../../store/hooks";
import {
  selectTranslatedString,
  selectCurrentLang,
} from "../../store/selectors/appSelectors";
import useGlobalNotification from "../../utils/hooks/useGlobalNotification";
import usePopup from "../../utils/hooks/usePopup";
import GiftCampaignModal from "../../popups/GiftCampaignModal";
import Icon from "../Icon/Icon";
import { useAuth } from "../../utils/hooks/useAuth";
import { IconColorE } from "../../models/enums";
import { clss } from "../../utils/helpers/styleHelpers";
import { isLanguageMatched } from "../../features/NewPrices2024/utils/notificationHelpers";

import styles from "./styles.module.scss";

interface CampaignButtonProps {
  hideTextOnMobile?: boolean;
  id?: string;
}

/**
 * Campaign button component for Header.
 * Shows campaign title text and navigates to campaigns page on click.
 * Visible from campaign start date to end date (same logic as campaign banner).
 * Cannot be closed (unlike campaign banner).
 */
const CampaignButton = memo((props: CampaignButtonProps) => {
  const { hideTextOnMobile, id } = props;
  const { currentUser } = useAuth();
  const { activeCampaignNotification } = useGlobalNotification();
  const translatedStrings = useAppSelector(selectTranslatedString);
  const currentLang = useAppSelector(selectCurrentLang);
  const { createPopup } = usePopup();

  // Show only for logged in users, matching language, and when campaign notification is active
  // Languages is required field, so it will always be present
  if (
    !currentUser ||
    !activeCampaignNotification ||
    !activeCampaignNotification.Languages ||
    !isLanguageMatched(currentLang, activeCampaignNotification.Languages)
  ) {
    return null;
  }

  const handleClick = () => {
    createPopup({
      body: <GiftCampaignModal />,
      className: `giftCampaignModal`,
    });
  };

  return (
    <button
      className={styles.campaignButton}
      onClick={handleClick}
      id={`button-gift-${id}`}
      name={`button-gift-${id}`}
    >
      <Icon name="gift" color={IconColorE.WHITE} />
      <span
        className={clss([
          styles.campaignButtonText,
          hideTextOnMobile
            ? styles.campaignButtonTextHiddenOnMobile
            : undefined,
        ])}
        dangerouslySetInnerHTML={{
          __html: translatedStrings?.[activeCampaignNotification.TitleConst],
        }}
      />
    </button>
  );
});

export default CampaignButton;
